// src/pages/JobsPage.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Spinner, Alert, Button, Toast, ToastContainer, Modal, Badge, Form, InputGroup } from 'react-bootstrap';
import { getJobsIds, getJobResults, getBatchStatus, deleteJobs, searchJobs, downloadOriginalImage, downloadOriginalsZip, batchReprocessJobs } from '../api/photoProcessingApi';
import { groupsAPI } from '../api/groupsApi';
import JobCard from '../components/Jobs/JobCard';
import ReprocessJobModal from '../components/ProcessingResults/ReprocessJobModal';
import './JobsPage.css';

const TAB_ALL  = '__all__';
const TAB_MINE = '__mine__';

const JobsPage = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [jobIds, setJobIds] = useState(new Set());
    const [jobs, setJobs] = useState([]);
    const [jobErrors, setJobErrors] = useState({});
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(null);
    const refreshIntervalRef = useRef(null);
    const [refreshingJobs, setRefreshingJobs] = useState(new Set());
    const loadingRef = useRef(false);
    const isMountedRef = useRef(true);

    // ── Tab state (own/shared filtering) ──────────────────────────────────────
    const [activeTab,     setActiveTab]     = useState(TAB_ALL);
    const [myGroups,      setMyGroups]      = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(true);

    // ── Search state ──────────────────────────────────────────────────────────
    const [searchQuery,       setSearchQuery]       = useState('');
    const [searchDateFrom,    setSearchDateFrom]    = useState('');
    const [searchDateTo,      setSearchDateTo]      = useState('');
    const [searchResults,     setSearchResults]     = useState([]);
    const [searchHasMore,     setSearchHasMore]     = useState(false);
    const [searchPage,        setSearchPage]        = useState(1);
    const [searchLoading,     setSearchLoading]     = useState(false);
    const [searchError,       setSearchError]       = useState(null);
    const [showSearchResults, setShowSearchResults] = useState(false);

    const searchContainerRef = useRef(null);
    const searchLoadingRef   = useRef(false);
    const searchHasMoreRef   = useRef(false);
    const searchPageRef      = useRef(1);
    const searchObserverRef  = useRef(null);
    const debounceTimer      = useRef(null);

    // Selection state
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedJobs, setSelectedJobs] = useState(new Set());
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [downloadingOriginals, setDownloadingOriginals] = useState(false);
    const [showReprocessModal, setShowReprocessModal] = useState(false);
    const [reprocessingJobs, setReprocessingJobs] = useState(false);

    // Toast state
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('success');

    // ── Derived: full job objects for the currently selected ids (for the
    // reprocess modal — needs thumbnail/inference_parameters, not just id) ──
    const selectedJobObjects = useMemo(
        () => jobs.filter(j => selectedJobs.has(j.id)),
        [jobs, selectedJobs]
    );

    // ── Derived: visible jobs (filtered by owner/group tab) ──────────────────
    const visibleJobs = useMemo(() => {
        if (activeTab === TAB_ALL)  return jobs;
        if (activeTab === TAB_MINE) return jobs.filter(j => j.is_owner);
        return jobs.filter(j =>
            Array.isArray(j.shared_groups) &&
            j.shared_groups.some(g => g.id === activeTab)
        );
    }, [jobs, activeTab]);

    // ── Derived: group tabs ────────────────────────────────────────────────────
    const groupTabs = useMemo(() => {
        const seen       = new Map();
        const myGroupIds = new Set(myGroups.map(g => g.id));
        jobs.forEach(job => {
            (job.shared_groups || []).forEach(g => {
                if (!seen.has(g.id)) seen.set(g.id, g);
            });
        });
        return [...seen.values()].filter(g => myGroupIds.has(g.id));
    }, [jobs, myGroups]);

    // ── Tab counts ─────────────────────────────────────────────────────────────
    const countForTab = useCallback((tab) => {
        if (tab === TAB_ALL)  return jobs.length;
        if (tab === TAB_MINE) return jobs.filter(j => j.is_owner).length;
        return jobs.filter(j =>
            (j.shared_groups || []).some(g => g.id === tab)
        ).length;
    }, [jobs]);

    // Load initial jobs
    useEffect(() => {
        isMountedRef.current = true;
        loadJobs(1, true);

        const fetchGroups = async () => {
            setGroupsLoading(true);
            try {
                const data = await groupsAPI.getMyGroups();
                if (isMountedRef.current) {
                    setMyGroups(Array.isArray(data) ? data : (data.results ?? []));
                }
            } catch (e) {
                console.error('Failed to load groups:', e);
            } finally {
                if (isMountedRef.current) setGroupsLoading(false);
            }
        };
        fetchGroups();

        return () => {
            isMountedRef.current = false;
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
            searchObserverRef.current?.disconnect();
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, []);

    // Refresh incomplete jobs periodically
    useEffect(() => {
        const incompleteJobs = jobs.filter(job =>
            job && !['completed', 'failed'].includes(job.status)
        );

        if (incompleteJobs.length === 0) {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
            return;
        }

        const checkIncompleteJobs = async () => {
            const incompleteIds = incompleteJobs.map(job => job.id);

            if (incompleteIds.length === 0) return;

            try {
                setRefreshingJobs(prev => new Set([...prev, ...incompleteIds]));

                // Get batch statuses
                const statuses = await getBatchStatus(incompleteIds);

                // For completed jobs, fetch full details
                const completedIds = incompleteIds.filter(id =>
                    statuses[id]?.status === 'completed'
                );

                let fullDetails = {};
                if (completedIds.length > 0) {
                    const detailResponses = await Promise.all(
                        completedIds.map(id => getJobResults(id))
                    );
                    detailResponses.forEach((response, index) => {
                        fullDetails[completedIds[index]] = response;
                    });
                }

                setJobs(prev => prev.map(job => {
                    const updatedStatus = statuses[job.id];
                    if (updatedStatus && isMountedRef.current) {
                        if (updatedStatus.status === 'completed' && fullDetails[job.id]) {
                            return { ...job, ...updatedStatus, ...fullDetails[job.id] };
                        }
                        return { ...job, ...updatedStatus };
                    }
                    return job;
                }));

            } catch (error) {
                console.error('Refresh error:', error);
            } finally {
                if (isMountedRef.current) {
                    setRefreshingJobs(prev => {
                        const newSet = new Set(prev);
                        incompleteIds.forEach(id => newSet.delete(id));
                        return newSet;
                    });
                }
            }
        };

        refreshIntervalRef.current = setInterval(checkIncompleteJobs, 5000);

        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        };
    }, [jobs]);

    const loadJobs = async (pageNum, isInitial = false) => {
        // Prevent concurrent loads
        if (loadingRef.current) return;

        try {
            loadingRef.current = true;
            if (isInitial) {
                setInitialLoading(true);
            } else {
                setLoading(true);
            }
            setError(null);

            const [responseJobIds, more] = await getJobsIds(pageNum);

            if (!isMountedRef.current) return;

            setHasMore(more);

            // Filter out already loaded job IDs
            const newIds = responseJobIds.filter(id => !jobIds.has(id));

            if (newIds.length === 0) {
                if (isInitial && responseJobIds.length === 0) {
                    setJobs([]);
                }
                return;
            }

            // Update IDs set
            setJobIds(prev => {
                const newSet = new Set(prev);
                newIds.forEach(id => newSet.add(id));
                return newSet;
            });

            // Load job details for new IDs only
            const results = await Promise.allSettled(
                newIds.map(async jobId => {
                    try {
                        const jobData = await getJobResults(jobId);
                        return { jobId, data: jobData, error: null };
                    } catch (error) {
                        console.error(`Error loading job ${jobId}:`, error);
                        return { jobId, data: null, error };
                    }
                })
            );

            if (!isMountedRef.current) return;

            const successfulJobs = results
                .filter(result => result.status === 'fulfilled' && result.value?.error === null)
                .map(result => result.value.data)
                .filter(job => job !== null);

            // Merge new jobs with existing ones (no duplicates)
            setJobs(prev => {
                const existingIds = new Set(prev.map(j => j.id));
                const newJobs = successfulJobs.filter(job => !existingIds.has(job.id));
                return [...prev, ...newJobs];
            });

        } catch (error) {
            console.error('Error loading jobs:', error);
            if (isMountedRef.current) {
                setError(error.message || "Failed to load jobs");
            }
        } finally {
            if (isMountedRef.current) {
                if (isInitial) {
                    setInitialLoading(false);
                } else {
                    setLoading(false);
                }
                loadingRef.current = false;
            }
        }
    };

    const loadMore = async () => {
        if (hasMore && !loading && !initialLoading && !loadingRef.current) {
            const nextPage = page + 1;
            setPage(nextPage);
            await loadJobs(nextPage, false);
        }
    };

    // ── Функция поиска ────────────────────────────────────────────────────────
    const performSearch = useCallback(async (query, pageNum, filterType, groupId, dateFrom, dateTo, isInitial = false) => {
        if (searchLoadingRef.current) return;

        try {
            searchLoadingRef.current = true;
            setSearchLoading(true);
            setSearchError(null);

            const params = { q: query };
            if (filterType) params.filter_type = filterType;
            if (groupId) params.group_id = groupId;
            if (pageNum) params.page = pageNum;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;

            const response = await searchJobs(params);
            if (!isMountedRef.current) return;

            const incoming = response.results ?? [];
            const more     = !!response.has_more;

            searchHasMoreRef.current = more;
            searchPageRef.current    = pageNum;

            setSearchHasMore(more);
            setSearchPage(pageNum);

            if (isInitial) {
                setSearchResults(incoming);
            } else {
                setSearchResults(prev => {
                    const existingIds = new Set(prev.map(j => j.id));
                    const fresh = incoming.filter(j => !existingIds.has(j.id));
                    return fresh.length ? [...prev, ...fresh] : prev;
                });
            }
        } catch (err) {
            console.error('Error searching jobs:', err);
            if (isMountedRef.current) setSearchError(err.message || 'Failed to search jobs');
        } finally {
            if (isMountedRef.current) {
                setSearchLoading(false);
                searchLoadingRef.current = false;
            }
        }
    }, []);

    // ── Функция для подгрузки следующей страницы поиска ─────────────────────
    const loadMoreSearch = useCallback(() => {
        if (searchLoadingRef.current || !searchHasMoreRef.current) return;

        const nextPage = searchPageRef.current + 1;
        const filterType = activeTab === TAB_ALL ? 'all'
                         : activeTab === TAB_MINE ? 'mine'
                         : undefined;
        const groupId = (activeTab !== TAB_ALL && activeTab !== TAB_MINE) ? activeTab : undefined;

        performSearch(
            searchQuery.trim(),
            nextPage,
            filterType,
            groupId,
            searchDateFrom,
            searchDateTo,
            false
        );
    }, [searchQuery, searchDateFrom, searchDateTo, activeTab, performSearch]);

    // ── Callback для последнего элемента поискового списка ──────────────────
    const searchLastItemRef = useCallback((node) => {
        if (searchObserverRef.current) {
            searchObserverRef.current.disconnect();
            searchObserverRef.current = null;
        }
        if (!node) return;
        searchObserverRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMoreSearch();
                }
            },
            { rootMargin: '200px' }
        );
        searchObserverRef.current.observe(node);
    }, [loadMoreSearch]);

    // ── Debounced поиск ────────────────────────────────────────────────────────
    useEffect(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        if (!searchQuery.trim() && !searchDateFrom && !searchDateTo) {
            setSearchResults([]);
            setSearchHasMore(false);
            setSearchPage(1);
            setShowSearchResults(false);
            return;
        }

        setShowSearchResults(true);

        debounceTimer.current = setTimeout(() => {
            const filterType = activeTab === TAB_ALL ? 'all'
                             : activeTab === TAB_MINE ? 'mine'
                             : undefined;
            const groupId = (activeTab !== TAB_ALL && activeTab !== TAB_MINE) ? activeTab : undefined;
            performSearch(
                searchQuery.trim(),
                1,
                filterType,
                groupId,
                searchDateFrom,
                searchDateTo,
                true
            );
        }, 300);

        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, [searchQuery, searchDateFrom, searchDateTo, activeTab, performSearch]);

    // ── Обработчик клика вне оверлея ─────────────────────────────────────────
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setShowSearchResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const onClick = (jobId) => {
        if (selectionMode) {
            toggleJobSelection(jobId);
        } else {
            navigate(`/processing-results/${jobId}`);
        }
    };

    // ── Обработчик смены таба ──────────────────────────────────────────────
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSelectionMode(false);
        setSelectedJobs(new Set());
    };

    // Selection functions
    const toggleSelectionMode = () => {
        setSelectionMode(!selectionMode);
        if (selectionMode) {
            setSelectedJobs(new Set());
        }
    };

    const toggleJobSelection = (jobId) => {
        setSelectedJobs(prev => {
            const newSet = new Set(prev);
            if (newSet.has(jobId)) {
                newSet.delete(jobId);
            } else {
                newSet.add(jobId);
            }
            return newSet;
        });
    };

    const selectAllJobs = () => {
        if (selectedJobs.size === visibleJobs.length) {
            setSelectedJobs(new Set());
        } else {
            setSelectedJobs(new Set(visibleJobs.map(job => job.id)));
        }
    };

    const handleDeleteJobs = async () => {
        try {
            setDeleting(true);
            const jobIdsArray = Array.from(selectedJobs);

            await deleteJobs(jobIdsArray);

            // Remove deleted jobs from state
            setJobs(prev => prev.filter(job => !selectedJobs.has(job.id)));
            setJobIds(prev => {
                const newSet = new Set(prev);
                selectedJobs.forEach(id => newSet.delete(id));
                return newSet;
            });

            setSelectedJobs(new Set());
            setSelectionMode(false);
            setShowDeleteModal(false);

            setToastMessage(`Successfully deleted ${jobIdsArray.length} job(s)`);
            setToastVariant('success');
            setShowToast(true);

        } catch (error) {
            console.error('Delete error:', error);
            setToastMessage('Failed to delete jobs: ' + (error.response?.data?.error || error.message));
            setToastVariant('danger');
            setShowToast(true);
        } finally {
            setDeleting(false);
        }
    };

    const handleOpenReprocessModal = () => {
        if (selectedJobs.size === 0) return;
        setShowReprocessModal(true);
    };

    const handleConfirmBatchReprocess = async (processingParams, yoloModelTypesByJobId) => {
        const jobIdsArray = Array.from(selectedJobs);
        if (jobIdsArray.length === 0) return;

        try {
            setReprocessingJobs(true);
            const result = await batchReprocessJobs(jobIdsArray, processingParams, yoloModelTypesByJobId);

            setShowReprocessModal(false);
            setSelectionMode(false);
            setSelectedJobs(new Set());

            // Optimistically flip reprocessed jobs to 'pending' locally: the
            // periodic refresh below (checkIncompleteJobs) only polls jobs
            // whose LOCAL status isn't already 'completed'/'failed' — without
            // this, jobs that were 'completed' or 'failed' before this batch
            // reprocess wouldn't be picked up until the next full page load.
            const reprocessedIds = new Set(result.reprocessed_ids || []);
            if (reprocessedIds.size > 0) {
                setJobs(prev => prev.map(job =>
                    reprocessedIds.has(job.id)
                        ? { ...job, status: 'pending', error_message: null }
                        : job
                ));
            }

            const skippedCount = result.skipped_ids?.length || 0;
            if (skippedCount === 0) {
                setToastMessage(`Queued ${result.reprocessed_count} job(s) for reprocessing`);
                setToastVariant('success');
            } else {
                setToastMessage(
                    `Queued ${result.reprocessed_count} of ${jobIdsArray.length} job(s) for reprocessing, ` +
                    `${skippedCount} skipped (not found or no edit permission)`
                );
                setToastVariant('warning');
            }
            setShowToast(true);
        } catch (error) {
            console.error('Batch reprocess error:', error);
            setToastMessage('Failed to reprocess jobs: ' + error.message);
            setToastVariant('danger');
            setShowToast(true);
        } finally {
            setReprocessingJobs(false);
        }
    };

    const handleDownloadSelectedOriginals = async () => {
        const jobIdsArray = Array.from(selectedJobs);
        if (jobIdsArray.length === 0) return;

        setDownloadingOriginals(true);

        try {
            if (jobIdsArray.length === 1) {
                // A single photo is saved as is, without a ZIP wrapper
                const id = jobIdsArray[0];
                const job = jobs.find(j => j.id === id);
                await downloadOriginalImage(id, job?.original_image_filename);
                setToastMessage('Downloading 1 original image');
                setToastVariant('success');
            } else {
                // Several photos: one request, one uncompressed ZIP, one save dialog
                const { included, skipped } = await downloadOriginalsZip(jobIdsArray);

                if (skipped > 0) {
                    setToastMessage(
                        `Downloaded ${included} of ${jobIdsArray.length} original image(s) as ZIP, ` +
                        `${skipped} skipped (not found or no access)`
                    );
                    setToastVariant('warning');
                } else {
                    setToastMessage(`Downloading ${included ?? jobIdsArray.length} original image(s) as ZIP`);
                    setToastVariant('success');
                }
            }
            setShowToast(true);
        } catch (error) {
            console.error('Download originals error:', error);
            setToastMessage('Failed to download originals: ' + error.message);
            setToastVariant('danger');
            setShowToast(true);
        } finally {
            setDownloadingOriginals(false);
        }
    };

    // Infinite scroll with intersection observer — наблюдает за последним
    // элементом ОТФИЛЬТРОВАННОГО (visibleJobs) списка; сама пагинация грузит
    // полный список с бэкенда (getJobsIds), таб лишь фильтрует уже
    // загруженное (как и в ExperimentsPage).
    const observerRef = useRef();
    const lastJobRef = useCallback(node => {
        if (loading || initialLoading) return;
        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !loading && !initialLoading) {
                loadMore();
            }
        });

        if (node) observerRef.current.observe(node);
    }, [loading, initialLoading, hasMore, activeTab]);

    if (initialLoading) {
        return (
            <Container className="mt-4 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading jobs...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <div className="container mt-4">
            {/* Header with selection controls */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Photos</h2>

                <div className="d-flex align-items-center gap-2">
                    <Button
                        variant={selectionMode ? "primary" : "outline-primary"}
                        onClick={toggleSelectionMode}
                        size="sm"
                    >
                        <i className="bi bi-check-square me-1"></i>
                        {selectionMode ? 'Cancel' : 'Select'}
                    </Button>

                    {selectionMode && (
                        <>
                            <Button
                                variant="outline-secondary"
                                onClick={selectAllJobs}
                                size="sm"
                                disabled={visibleJobs.length === 0}
                            >
                                {selectedJobs.size === visibleJobs.length ? 'Deselect All' : 'Select All'}
                            </Button>

                            {selectedJobs.size > 0 && (
                                <Badge bg="primary" className="ms-2">
                                    {selectedJobs.size} selected
                                </Badge>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Action buttons when jobs are selected */}
            {selectionMode && selectedJobs.size > 0 && (
                <div className="mb-4 p-3 bg-light rounded">
                    <div className="d-flex gap-2 flex-wrap">
                        <Button
                            variant="danger"
                            onClick={() => setShowDeleteModal(true)}
                            size="sm"
                        >
                            <i className="bi bi-trash me-1"></i>
                            Delete ({selectedJobs.size})
                        </Button>

                        <Button
                            variant="outline-warning"
                            onClick={handleOpenReprocessModal}
                            disabled={reprocessingJobs}
                            size="sm"
                        >
                            <i className="bi bi-arrow-repeat me-1"></i>
                            Reprocess ({selectedJobs.size})
                        </Button>

                        <Button
                            variant="outline-success"
                            onClick={handleDownloadSelectedOriginals}
                            disabled={downloadingOriginals}
                            size="sm"
                        >
                            {downloadingOriginals ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-1" />
                                    Downloading...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-download me-1"></i>
                                    Download originals ({selectedJobs.size})
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            {/* ── Поисковая строка с фильтрами по дате ── */}
            <div className="jp__search-wrapper" ref={searchContainerRef}>
                <div className="jp__search-row">
                    <InputGroup className="jp__search-input">
                        <InputGroup.Text>
                            <i className="bi bi-search" />
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Search photos by filename, user email, or name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => {
                                if (searchQuery.trim() || searchDateFrom || searchDateTo) {
                                    setShowSearchResults(true);
                                }
                            }}
                        />
                        {searchQuery && (
                            <Button
                                variant="outline-secondary"
                                onClick={() => setSearchQuery('')}
                                title="Clear search"
                            >
                                <i className="bi bi-x-lg" />
                            </Button>
                        )}
                    </InputGroup>

                    <div className="jp__search-dates">
                        <Form.Control
                            type="date"
                            value={searchDateFrom}
                            onChange={(e) => setSearchDateFrom(e.target.value)}
                            placeholder="From"
                            size="sm"
                        />
                        <span className="jp__search-dates-sep">—</span>
                        <Form.Control
                            type="date"
                            value={searchDateTo}
                            onChange={(e) => setSearchDateTo(e.target.value)}
                            placeholder="To"
                            size="sm"
                        />
                    </div>
                </div>

                {/* ── Оверлей с результатами поиска ── */}
                {showSearchResults && (
                    <div className="jp__search-overlay">
                        {searchLoading && searchResults.length === 0 && (
                            <div className="jp__search-loading">
                                <Spinner animation="border" size="sm" />
                                <span>Searching…</span>
                            </div>
                        )}
                        {searchError && (
                            <div className="jp__search-error">{searchError}</div>
                        )}
                        {!searchLoading && !searchError && searchResults.length === 0 && (
                            <div className="jp__search-empty">
                                <i className="bi bi-search" />
                                <span>No photos found</span>
                            </div>
                        )}
                        {searchResults.length > 0 && (
                            <div className="jp__search-list">
                                {searchResults.map((job, index) => {
                                    const isLast = index === searchResults.length - 1;
                                    return (
                                        <div
                                            key={job.id}
                                            className="jp__search-item"
                                            ref={isLast ? searchLastItemRef : null}
                                            onClick={() => {
                                                navigate(`/processing-results/${job.id}`);
                                                setShowSearchResults(false);
                                            }}
                                        >
                                            <div className="jp__search-item-main">
                                                <div className="jp__search-item-name">
                                                    {job.original_image_filename || 'Untitled'}
                                                </div>
                                                <div className="jp__search-item-meta">
                                                    <span>{job.user_email}</span>
                                                    <span className="jp__search-item-date">
                                                        {new Date(job.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {searchLoading && searchResults.length > 0 && (
                                    <div className="jp__search-load-more">
                                        <Spinner animation="border" size="sm" />
                                        <span>Loading more…</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Tab bar ── */}
            <div className="jp__tabs" role="tablist">
                <button
                    role="tab" aria-selected={activeTab === TAB_ALL}
                    className={`jp__tab ${activeTab === TAB_ALL ? 'jp__tab--active' : ''}`}
                    onClick={() => handleTabChange(TAB_ALL)}
                >
                    <i className="bi bi-grid me-1" />All
                    <span className="jp__tab-count">{countForTab(TAB_ALL)}</span>
                </button>

                <button
                    role="tab" aria-selected={activeTab === TAB_MINE}
                    className={`jp__tab ${activeTab === TAB_MINE ? 'jp__tab--active' : ''}`}
                    onClick={() => handleTabChange(TAB_MINE)}
                >
                    <i className="bi bi-person me-1" />Mine
                    <span className="jp__tab-count">{countForTab(TAB_MINE)}</span>
                </button>

                {groupTabs.length > 0 && <div className="jp__tab-divider" aria-hidden="true" />}

                {groupsLoading && (
                    <span className="jp__tab-loading">
                        <Spinner animation="border" size="sm" />
                    </span>
                )}

                {!groupsLoading && groupTabs.map(group => (
                    <button
                        key={group.id} role="tab"
                        aria-selected={activeTab === group.id}
                        className={`jp__tab jp__tab--group ${activeTab === group.id ? 'jp__tab--active' : ''}`}
                        onClick={() => handleTabChange(group.id)}
                        title={group.name}
                    >
                        <i className="bi bi-people me-1" />
                        <span className="jp__tab-label">{group.name}</span>
                        <span className="jp__tab-count">{countForTab(group.id)}</span>
                    </button>
                ))}
            </div>

            {/* ── Group context banner ── */}
            <ActiveTabBanner activeTab={activeTab} groupTabs={groupTabs} count={visibleJobs.length} />

            {visibleJobs.length > 0 ? (
                <Row>
                    {visibleJobs.map((job, index) => (
                        <Col
                            key={job.id}
                            xs={12} sm={6} md={4} lg={3} className="mb-4"
                            ref={index === visibleJobs.length - 1 ? lastJobRef : null}
                        >
                            <JobCard
                                job={job}
                                isRefreshing={refreshingJobs.has(job.id)}
                                onClick={() => onClick(job.id)}
                                selectionMode={selectionMode}
                                isSelected={selectedJobs.has(job.id)}
                                onToggleSelect={() => toggleJobSelection(job.id)}
                                showOwnerBadge={activeTab !== TAB_MINE}
                                showGroupBadges={activeTab === TAB_ALL || activeTab === TAB_MINE}
                            />
                        </Col>
                    ))}
                </Row>
            ) : (
                <EmptyState
                    activeTab={activeTab}
                    groupTabs={groupTabs}
                />
            )}

            {/* Loading indicator for more items */}
            {loading && (
                <div className="text-center my-4">
                    <Spinner animation="border" size="sm" role="status">
                        <span className="visually-hidden">Loading more...</span>
                    </Spinner>
                </div>
            )}

            {/* No more items message */}
            {!hasMore && visibleJobs.length > 0 && !loading && (
                <div className="text-center my-4">
                    <p className="text-muted">No more photos to load</p>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <i className="bi bi-exclamation-triangle text-warning" style={{fontSize: '3rem'}}></i>
                        <h5 className="mt-3">Are you sure?</h5>
                        <p className="text-muted">
                            You are about to delete <strong>{selectedJobs.size}</strong> job(s).
                            This action cannot be undone.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteModal(false)}
                        disabled={deleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteJobs}
                        disabled={deleting}
                    >
                        {deleting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-trash me-1"></i>
                                Delete {selectedJobs.size} Job(s)
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Batch Reprocess Modal */}
            <ReprocessJobModal
                show={showReprocessModal}
                onHide={() => setShowReprocessModal(false)}
                mode="batch"
                jobs={selectedJobObjects}
                onConfirm={handleConfirmBatchReprocess}
                submitting={reprocessingJobs}
            />

            {/* Toast notifications */}
            <ToastContainer position="bottom-end" className="p-3">
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    delay={4000}
                    autohide
                    bg={toastVariant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toastVariant === 'success' ? 'Success' :
                             toastVariant === 'danger' ? 'Error' : 'Info'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toastVariant === 'danger' ? 'text-white' : ''}>
                        {toastMessage}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </div>
    );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const ActiveTabBanner = ({ activeTab, groupTabs, count }) => {
    if (activeTab === TAB_ALL || activeTab === TAB_MINE) return null;
    const group = groupTabs.find(g => g.id === activeTab);
    if (!group) return null;
    return (
        <div className="jp__tab-banner">
            <i className="bi bi-people-fill me-2" />
            Showing <strong>{count}</strong> photo{count !== 1 ? 's' : ''} shared
            with <strong>{group.name}</strong>
        </div>
    );
};

const EmptyState = ({ activeTab, groupTabs }) => {
    if (activeTab === TAB_ALL) return (
        <div className="jp__empty">
            <i className="bi bi-images jp__empty-icon" />
            <h5>No photos yet</h5>
            <p>Photos you upload for processing will appear here</p>
        </div>
    );
    if (activeTab === TAB_MINE) return (
        <div className="jp__empty">
            <i className="bi bi-person-x jp__empty-icon" />
            <h5>No photos owned by you</h5>
            <p>Photos you upload will appear here</p>
        </div>
    );
    const group = groupTabs.find(g => g.id === activeTab);
    return (
        <div className="jp__empty">
            <i className="bi bi-share jp__empty-icon" />
            <h5>No photos shared with {group?.name ?? 'this group'}</h5>
            <p>Share photos, or experiments that include them, to make them visible here</p>
        </div>
    );
};

export default JobsPage;
